import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Layout from '../../elements/Layout';
import moment from 'moment';
import { fetchContractsReports, fetchCustomers } from '../../utils/api/api';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const ContractsReports = () => {
    const [filters, setFilters] = useState({
        type: '',
        customer_id: '',
    });
    const [customers, setCustomers] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const loadCustomers = async () => {
            try {
                const response = await fetchCustomers({
                    page: 0,
                    pageSize: 0,
                });
                const formattedCustomers = response.customers.map(customer => ({
                    value: customer.id,
                    label: `${customer.first_name} ${customer.last_name}`,
                }));
                setCustomers(formattedCustomers);
            } catch (error) {
                console.error('Failed to fetch customers.', error);
            }
        };
        loadCustomers();
    }, []);

    const fetchContractsData = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchContractsReports(filters);
            setContracts(data.contracts || []);
        } catch (err) {
            setError('Failed to fetch contracts. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContractsData();
    }, [filters]);

    const handleSelectChange = (name, value) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(
            contracts.map((contract) => ({
                Type: contract?.type?.replace('_', ' '),
                Customer: `${contract?.customer?.first_name} ${contract?.customer?.last_name}`,
                Status: contract?.status,
                Date: moment(contract.createdAt).format('MMM DD, YYYY, HH:MM A'),
            }))
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Contracts');
        XLSX.writeFile(wb, 'contracts_report.xlsx');
    };

    return (
        <Layout>
            <div className="min-h-screen">
                <div className="bg-white p-4 border border-gray-200 rounded-md mb-4 flex items-center justify-between w-full">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Contract Type</label>
                            <select
                                name="type"
                                value={filters.type}
                                onChange={(e) => handleSelectChange('type', e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                            >
                                <option value="">All</option>
                                <option value="offer_letter">Offer Letter</option>
                                <option value="sales_agreement">Sales Agreement</option>
                                <option value="completion_letter">Completion Letter</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Customer</label>
                            <Select
                                options={customers}
                                onChange={(selected) => handleSelectChange('customer_id', selected ? selected.value : '')}
                                isClearable
                                placeholder="Select customer"
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <button
                        onClick={exportToExcel}
                        className="bg-primary text-white px-4 py-1.5 text-[14px] rounded-md hover:bg-blue-700"
                    >
                        Export to Excel
                    </button>
                </div>

                <div className="bg-white border border-gray-200 rounded-md">
                    {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
                    {!loading && contracts.length === 0 && <p className="text-gray-500">No contracts found.</p>}
                    {loading && <p className="text-gray-500">Loading...</p>}
                    {contracts.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full table-auto border-collapse rounded-md">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-700">
                                        <th className="border-b text-[18px] font-normal border-gray-200 text-start px-4 py-2">#</th>
                                        <th className="border-b text-[18px] font-normal border-gray-200 text-start px-4 py-2">Type</th>
                                        <th className="border-b text-[18px] font-normal border-gray-200 text-start px-4 py-2">Customer</th>
                                        <th className="border-b text-[18px] font-normal border-gray-200 text-start px-4 py-2">Status</th>
                                        <th className="border-b text-[18px] font-normal border-gray-200 text-start px-4 py-2">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contracts.map((contract, index) => (
                                        <tr key={contract.id} className="hover:bg-gray-50 text-gray-500">
                                            <td className="border-b text-[13px] text-black font-light border-gray-200 px-4 py-2">{index + 1}</td>
                                            <td 
                                                onClick={() => navigate(`/dashboard/contracts/${contract.id}/view`)} 
                                                className="border-b text-[13px] text-black font-light border-gray-200 px-4 py-2 capitalize hover:text-secondary hover:cursor-pointer hover:font-medium  "
                                            >
                                                {contract?.type?.replace('_', ' ')}
                                            </td>
                                            <td
                                                className="border-b text-[13px] text-black font-light border-gray-200 px-4 py-2 lowercase hover:text-secondary hover:cursor-pointer hover:font-medium"
                                                onClick={() => navigate(`/dashboard/customers/${contract.customer.id}/view`)}
                                            >
                                                {contract?.customer
                                                    ? `${contract.customer.first_name} ${contract.customer.last_name}`
                                                        .split(' ')
                                                        .slice(0, 2)
                                                        .join(' ')
                                                    : 'N/A'}
                                            </td>
                                            <td className="border-b text-[13px] text-gray-500 border-gray-200 px-4 py-2">
                                                <span className={`${contract?.status === 'approved' ? 'bg-green-500 text-white py-1 px-2 rounded-md' : 'bg-red-500 text-white py-1 px-2 rounded-md'}`}>{contract.status}</span>
                                            </td>
                                            <td className="border-b text-[13px] text-black font-light border-gray-200 px-4 py-2">
                                                {moment(contract.createdAt).format('MMM DD, YYYY, HH:MM A')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default ContractsReports;
